import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import RealTime from './RealTime'

const RTConversations = () => {
  const queryClient = useQueryClient()

  const safeMerge = (existing, incoming) => {
    const merged = { ...existing, ...incoming }
    if (!incoming.lastMessage?.id && existing.lastMessage?.id) {
      merged.lastMessage = existing.lastMessage
    }
    return merged
  }

  const updateConversation = newData => {
    const queryKey = ['Conversation', `${newData.id}`]

    queryClient.setQueryData(queryKey, conversation => {
      if (!conversation) return conversation
      return safeMerge(conversation, newData)
    })
  }

  const updateConversations = (open, newData) => {
    queryClient.setQueryData(
      ['Conversations', open ? 'open' : 'closed', ''],
      conversations => {
        if (!conversations) return conversations
        const { pageParams, pages = [] } = conversations
        return {
          pageParams,
          pages: pages.map(page => {
            const { records, ...others } = page
            const updatedRecords = records.map(record =>
              record.id === newData.id ? safeMerge(record, newData) : record
            )
            return { ...others, records: updatedRecords }
          }),
        }
      }
    )
  }

  const addConversation = (status = 'open', newData) => {
    queryClient.setQueryData(['Conversations', status, ''], conversations => {
      if (!conversations) return conversations
      const { pageParams, pages = [] } = conversations

      let existingRecord = null
      for (const page of pages) {
        const found = page.records?.find(r => r.id === newData.id)
        if (found) {
          existingRecord = found
          break
        }
      }

      const mergedData = existingRecord
        ? safeMerge(existingRecord, newData)
        : newData

      return {
        pageParams,
        pages: pages.map(page => {
          const { records, ...others } = page
          const filteredRecords = records.filter(
            record => record.id !== newData.id
          )
          return {
            ...others,
            records: [mergedData, ...filteredRecords],
          }
        }),
      }
    })
  }

  const removeConversation = (status = 'open', newData) => {
    queryClient.setQueryData(['Conversations', status, ''], conversations => {
      if (!conversations) return conversations
      const { pageParams, pages = [] } = conversations
      return {
        pageParams,
        pages: pages.map(({ records, ...others }) => ({
          ...others,
          records: records.filter(({ id }) => id !== newData.id),
        })),
      }
    })
  }

  // The open conversation refetches itself through RTMessages.
  const refetchConversations = () => {
    queryClient.invalidateQueries({ queryKey: ['Conversations'] })
  }

  const handleEvent = ({ action, subject, data }) => {
    if (!data?.id) return
    if (action === 'update') {
      switch (subject) {
        case 'opened':
          updateConversation(data)
          removeConversation('closed', data)
          addConversation('open', data)
          break
        case 'closed':
          updateConversation(data)
          removeConversation('open', data)
          addConversation('closed', data)
          break
        case 'readed':
        case 'unreaded':
        case 'flagged':
        case 'unflagged':
        // Somebody flipped the per-conversation reschedule switch in another
        // tab. Metadata, like flagging: merge it and leave the inbox alone. The
        // default branch below would prepend the thread to the open list, which
        // is the answer for a new message and not for a setting.
        case 'reschedule_enabled':
        case 'reschedule_disabled': {
          const { lastMessage: _, ...metadata } = data
          updateConversation(metadata)
          updateConversations(data.open, metadata)
          break
        }
        case 'status_updated':
        case 'message_removed':
          updateConversation(data)
          updateConversations(data.open, data)
          break
        case 'new_message_reminder':
        case 'opened_and_unread':
          if (data.unreadMessagesCount > 1) {
            queryClient.invalidateQueries({
              queryKey: ['Conversation', `${data.id}`],
              refetchType: 'all',
            })
          } else {
            updateConversation(data)
          }
          removeConversation('open', data)
          addConversation('open', data)
          break
        default:
          updateConversation(data)
          removeConversation('open', data)
          addConversation('open', data)
          break
      }
    }
  }

  return (
    <RealTime
      channel="Chat::ConversationsChannel"
      onEvent={handleEvent}
      onReconnect={refetchConversations}
    />
  )
}

export default RTConversations
